import {
    MutableRefObject,
    ReactNode,
    RefCallback,
    RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { TriggeredPopup } from 'shared/triggered_popup/TriggeredPopup';
import { updateRef } from 'utils/mergeRefs';
import { useDefer } from 'utils/useDefer';

const DefaultShowDelayMs = 0;
const DefaultHideDelayMs = 200;

// Global ref to store the hide function of the current visible tooltip
// so that the tooltip can be hidden before showing another tooltip.
const globalHideTooltip: MutableRefObject<((immediate: boolean) => void) | null> = {
    current: null,
};

export type UserHoverTooltipProps = Readonly<{
    /**
     * Ref of the element the tooltip should be anchored to and triggered by.
     */
    anchorRef: RefObject<HTMLElement>;
    /**
     * The amount of time in milliseconds after the pointer enters the trigger
     * before showing the tooltip (default `0`).
     */
    showDelayMs?: number;
    /**
     * The amount of time in milliseconds after the pointer leaves the trigger
     * before hiding the tooltip (default `200`).
     */
    hideDelayMs?: number;
    /**
     * If the tooltip should remain visible if the pointer enters the tooltip.
     * (default `false`)
     */
    stickyHover?: boolean;
    /**
     * Removes the default padding inside the tooltip.
     */
    noPadding?: boolean;
    /**
     * The content to render inside of the tooltip.
     */
    children: ReactNode;
}>;

export function HoverTooltip({
    anchorRef,
    children,
    showDelayMs = DefaultShowDelayMs,
    hideDelayMs = DefaultHideDelayMs,
    stickyHover = false,
    noPadding = false,
}: UserHoverTooltipProps) {
    const defer = useDefer();
    const showTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const [isVisible, setIsVisible] = useState(false);
    const popupRef = useRef<HTMLDivElement>(null);

    const isVisibleRef = useRef(isVisible);
    isVisibleRef.current = isVisible;

    const showDelayMsRef = useRef(showDelayMs);
    showDelayMsRef.current = showDelayMs;

    const hideDelayMsRef = useRef(hideDelayMs);
    hideDelayMsRef.current = hideDelayMs;

    const preventHideOnHoverRef = useRef(stickyHover);
    preventHideOnHoverRef.current = stickyHover;

    const scheduleHide = useCallback(
        (immediate: boolean) => {
            // If scheduled to show, cancel
            if (showTimeoutRef.current) {
                window.clearTimeout(showTimeoutRef.current);
                showTimeoutRef.current = undefined;
            }
            // If visible and not scheduled to hide or force hide, schedule to hide
            if (isVisibleRef.current && (!hideTimeoutRef.current || immediate)) {
                hideTimeoutRef.current = defer(
                    () => {
                        setIsVisible(false);
                        hideTimeoutRef.current = undefined;
                    },
                    immediate ? 0 : hideDelayMsRef.current
                );
            }
        },
        [defer]
    );

    const scheduleHideRef = useRef(scheduleHide);
    scheduleHideRef.current = scheduleHide;

    const scheduleShow = useCallback(() => {
        // If scheduled to hide, cancel
        if (hideTimeoutRef.current) {
            window.clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = undefined;
        }
        // If not visible, and not scheduled to show, schedule to show
        if (!isVisibleRef.current && !showTimeoutRef.current) {
            showTimeoutRef.current = defer(() => {
                setIsVisible(true);
                if (
                    globalHideTooltip.current &&
                    globalHideTooltip.current !== scheduleHideRef.current
                ) {
                    globalHideTooltip.current(true);
                }
                globalHideTooltip.current = scheduleHideRef.current;
                showTimeoutRef.current = undefined;
            }, showDelayMsRef.current);
        }
    }, [defer]);

    const scheduleShowRef = useRef(scheduleShow);
    scheduleShowRef.current = scheduleShow;

    // Add listeners to popup so that mouse enter will prevent tooltip
    // from hiding, and mouse leave will schedule tooltip hide
    const registerPopupListeners = useCallback<RefCallback<HTMLDivElement>>((elem) => {
        const popupElem = elem?.parentElement;
        updateRef(popupRef, popupElem);
        if (!popupElem) {
            return;
        }
        if (preventHideOnHoverRef.current) {
            popupElem.addEventListener('mouseenter', () => scheduleShowRef.current());
            popupElem.addEventListener('mouseleave', () =>
                scheduleHideRef.current(false)
            );
        } else {
            // Prevent popup from triggering blur events
            popupElem.style.pointerEvents = 'none';
        }
    }, []);

    // Add listeners to anchor element so that mouse enter will trigger tooltip
    // show, and mouse leave will schedule tooltip hide
    useEffect(() => {
        const anchor = anchorRef.current;
        if (!anchor) {
            throw new Error('Missing anchor element');
        }
        const show = () => scheduleShowRef.current();
        const hide = () => scheduleHideRef.current(false);

        anchor.addEventListener('mouseenter', show);
        anchor.addEventListener('mouseleave', hide);

        return () => {
            anchor.removeEventListener('mouseenter', show);
            anchor.removeEventListener('mouseleave', hide);
        };
    }, [anchorRef, scheduleHide, scheduleShow]);

    // Clear global tooltip hide if this tooltip is unmounting
    // and the hide function belongs to this tooltip
    useEffect(() => {
        if (globalHideTooltip.current === scheduleHideRef.current) {
            globalHideTooltip.current = null;
        }
    }, []);

    return isVisible ? (
        <TriggeredPopup
            ref={registerPopupListeners}
            anchorRef={anchorRef}
            onClose={() => undefined}
            noPadding={noPadding}
            test-id="HoverTooltip__Popup"
        >
            {children}
        </TriggeredPopup>
    ) : (
        <></>
    );
}
